/**
 * StagePie — the pipeline, as one glyph.
 *
 * Six wedges, one per shared pipeline stage (`WORK_PIPELINE_STAGES`), read
 * clockwise from twelve o'clock: Proposed · Plan · Build · Review · QA · Done.
 * Filled wedges are behind the work, the full one is the stage it stands in,
 * and the hollow ones have not happened. That is the whole vocabulary, and it
 * is the same one the list sections and the case file read, never a second
 * one grown inside a component.
 *
 * The WEDGES take the stage's own hue (`--stage-<stage>` in tokens.css), so a
 * row in Plan is indigo and a row in Review is magenta before a word is read;
 * the stages behind the work print in a tint of that same hue. Finished work
 * reads its OUTCOME instead of a hue: the whole pie green when it shipped,
 * flat gray when it closed with no work landed, and the stage's own wedge red
 * when its review or QA verdict failed. The hue rides one custom property
 * (`--stage`) set here from the stage, never by a caller.
 *
 * The RING is the second fact, and the only one that moves: it turns green
 * while an agent is actually working, breathes blue while a person is owed a
 * move, holds dotted and still in amber when the last run stopped, and is
 * absent entirely for finished, past and future reads. Its colour follows the
 * house rule — blue is human attention, green is an agent at work, amber is
 * the product telling on itself — and rides `--tone`, so the ring can never
 * take a colour the state did not earn, and a wedge can never take the ring's.
 *
 * No motion at all under `prefers-reduced-motion`.
 *
 * @example
 * <StagePie stage="build" mode="live" />
 * <StagePie stage="plan" mode="wait" size={18} />
 * <StagePie stage="qa" mode="future" label="QA, not yet" />
 */
import type { CSSProperties } from "react";
import {
  JOB_STAGES,
  WORK_PIPELINE_STAGES,
  type WorkPipelineStage as PipelineStage,
} from "@yagni/shared";
import { cn } from "./_utils";
import "./stagePie.css";

/**
 * What the glyph is SAYING, which is never only where the work stands:
 *
 *   live     an agent is working right now (green, the ring turns)
 *   wait     a person is owed a move (blue, the ring breathes)
 *   stop     the last run ended and nothing replaced it (amber, dotted, still)
 *   settled  finished: every wedge inked, no ring
 *   past     where the work stands with nothing happening to it, or a stage it
 *            has already been through (filled to the stage, no ring)
 *   future   a stage that has not happened (hollow, no ring)
 *   passed   shipped: every wedge in the success green, no ring
 *   canceled closed with no work landed: every wedge a flat gray, no ring
 *   failed   its own review or QA verdict failed: that wedge in the error red
 */
export type StagePieMode =
  | "live"
  | "wait"
  | "stop"
  | "settled"
  | "past"
  | "future"
  | "passed"
  | "canceled"
  | "failed";

/** The tone each moving mode paints with. The still modes take none. */
export const STAGE_PIE_TONE: Record<StagePieMode, string | null> = {
  live: "var(--accent-active)",
  wait: "var(--accent)",
  stop: "var(--honesty)",
  settled: null,
  past: null,
  passed: null,
  canceled: null,
  failed: null,
  future: null,
};

/** The hue each stage's wedges print in. Tokens, so a theme can retune them. */
export const STAGE_HUE: Record<PipelineStage, string> = {
  proposed: "var(--stage-proposed)",
  plan: "var(--stage-plan)",
  build: "var(--stage-build)",
  review: "var(--stage-review)",
  qa: "var(--stage-qa)",
  done: "var(--stage-done)",
};

export interface StagePieProps {
  /** Where the work stands. Null draws the empty pie. */
  stage: PipelineStage | null;
  mode: StagePieMode;
  /** Rendered size in px. 26 on a list row, 20 in the kicker and its popover rows. */
  size?: number;
  /** Override the announced name. Defaults to "<Stage> · <mode>". */
  label?: string;
  className?: string;
}

const CENTER = 12;
const RADIUS = 8;

/** One wedge: 60° of the circle, starting at twelve o'clock and going clockwise. */
function wedgePath(index: number): string {
  const point = (degrees: number) => {
    const radians = (degrees * Math.PI) / 180;
    const x = CENTER + RADIUS * Math.cos(radians);
    const y = CENTER + RADIUS * Math.sin(radians);
    return `${Math.round(x * 100) / 100} ${Math.round(y * 100) / 100}`;
  };
  return `M12 12 L${point(-90 + 60 * index)} A 8 8 0 0 1 ${point(-90 + 60 * (index + 1))} Z`;
}

/** The six wedge paths, one per stage of the shared pipeline. */
const WEDGES = WORK_PIPELINE_STAGES.map((stage, index) => ({ key: stage, d: wedgePath(index) }));

/** The stage's own word, from the shared stage list the Team page reads too. */
function stageLabel(stage: PipelineStage): string {
  return JOB_STAGES.find((entry) => entry.id === stage)?.label ?? stage;
}

/** Whether one wedge is behind the work, the work itself, or still to come. */
function wedgeFill(index: number, current: number, mode: StagePieMode): string {
  if (mode === "settled" || mode === "passed" || mode === "canceled") return "done";
  if (mode === "future" || current < 0) return "empty";
  if (index < current) return "done";
  if (index === current) return "now";
  return "empty";
}

export function StagePie({ stage, mode, size = 26, label, className }: StagePieProps) {
  const current = stage ? WORK_PIPELINE_STAGES.indexOf(stage) : -1;
  const tone = STAGE_PIE_TONE[mode];
  const name = label ?? (stage ? `${stageLabel(stage)} · ${mode}` : mode);
  const style: Record<string, string> = {};
  if (stage) style["--stage"] = STAGE_HUE[stage];
  if (tone) style["--tone"] = tone;
  return (
    <svg
      className={cn("sp", `is-${mode}`, className)}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label={name}
      style={stage || tone ? (style as CSSProperties) : undefined}
    >
      {WEDGES.map((wedge, index) => (
        <path
          key={wedge.key}
          className={`sp-w is-${wedgeFill(index, current, mode)}`}
          d={wedge.d}
        />
      ))}
      {tone && <circle className="sp-ring" cx="12" cy="12" r="11" />}
    </svg>
  );
}
